import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import buildImageUrl from '../config/imageUrlHelper';

const FLATICON_LIBRARY_ICON = 'https://cdn-icons-png.flaticon.com/512/9037/9037879.png';
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const ImageUploadField = ({
  value,
  tempToken,
  onChange,
  disabled = false,
  previewMaxHeightClass = 'max-h-52',
  flatWhenEmpty = false
}) => {
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState(tempToken ? 'upload' : 'url');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadPreviewPath, setUploadPreviewPath] = useState('');

  useEffect(() => {
    if (tempToken) {
      setMode('upload');
    }
  }, [tempToken]);

  const previewSrc = useMemo(() => {
    if (uploadPreviewPath) {
      return buildImageUrl(uploadPreviewPath);
    }

    if (value) {
      return buildImageUrl(value);
    }

    return '';
  }, [uploadPreviewPath, value]);

  const isFlatState = flatWhenEmpty && !previewSrc;

  const handleUrlChange = (event) => {
    const url = event.target.value.trim();

    if (!url) {
      setError('');
      setUploadPreviewPath('');
      onChange('', '');
      return;
    }

    try {
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        throw new Error('Protocolo no permitido');
      }

      setError('');
      setUploadPreviewPath('');
      onChange(url, '');
    } catch (validationError) {
      setError('Introduce una URL valida (http/https).');
    }
  };

  const validateClientFile = (file) => {
    if (!file) return 'No se selecciono ningun archivo.';
    if (!file.type || !file.type.startsWith('image/')) {
      return 'Debes seleccionar un archivo de imagen.';
    }
    if (file.size <= 0 || file.size > MAX_IMAGE_SIZE_BYTES) {
      return 'La imagen debe pesar entre 1 byte y 5 MB.';
    }
    return '';
  };

  const uploadFile = async (file) => {
    const validationError = validateClientFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const payload = new FormData();
      payload.append('question_image', file);

      const response = await axios.post(`${API_BASE_URL}/subir_imagen_pregunta.php`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      const receivedToken = response?.data?.imagen_temp_token || '';
      const tempRelativePath = response?.data?.temp_relative_path || '';

      if (!receivedToken || !tempRelativePath) {
        throw new Error('Respuesta invalida del servidor al subir imagen.');
      }

      setUploadPreviewPath(tempRelativePath);
      onChange('', receivedToken);
    } catch (uploadError) {
      const apiErrorMessage = uploadError?.response?.data?.error;
      setError(apiErrorMessage || 'No se pudo subir la imagen. Intentalo de nuevo.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      await uploadFile(selectedFile);
    }
    event.target.value = '';
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    if (disabled || isUploading) return;

    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      await uploadFile(droppedFile);
    }
  };

  const handleRemoveImage = () => {
    setError('');
    setUploadPreviewPath('');
    onChange('', '');
  };

  return (
    <div className={`rounded-xl p-3 min-h-[220px] flex flex-col transition-colors duration-200 ${
      isFlatState
        ? 'border border-transparent bg-transparent dark:bg-transparent'
        : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
    }`}>
      <div className="flex items-start gap-3 mb-2">
        <img
          src={FLATICON_LIBRARY_ICON}
          alt="Icono de biblioteca"
          className="w-8 h-8 object-contain"
          loading="lazy"
        />
        <div>
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Imagen</h4>
        </div>
      </div>

      <div className="flex flex-col gap-2 mb-2 shrink-0">
        <div className="grid grid-cols-2 gap-1">
          <button
            type="button"
            disabled={disabled || isUploading}
            onClick={() => {
              setMode('url');
              setError('');
            }}
            className={`h-9 px-2 py-1.5 text-xs rounded-md border transition-colors whitespace-nowrap ${mode === 'url' ? 'bg-blue-600 text-white border-blue-600' : 'bg-transparent text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}
          >
            URL
          </button>
          <button
            type="button"
            disabled={disabled || isUploading}
            onClick={() => {
              setMode('upload');
              setError('');
            }}
            className={`h-9 px-2 py-1.5 text-xs rounded-md border transition-colors whitespace-nowrap ${mode === 'upload' ? 'bg-blue-600 text-white border-blue-600' : 'bg-transparent text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}
          >
            Subir archivo
          </button>
        </div>

        <div className="relative h-10 min-w-0 w-full">
          <input
            type="text"
            value={tempToken ? '' : value || ''}
            onChange={handleUrlChange}
            disabled={disabled || isUploading || mode !== 'url'}
            placeholder="https://ejemplo.com/imagen.jpg"
            className={`absolute inset-0 w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-opacity ${mode === 'url' ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={handleFileInputChange}
            disabled={disabled || isUploading}
          />
          <button
            type="button"
            disabled={disabled || isUploading || mode !== 'upload'}
            onClick={() => fileInputRef.current?.click()}
            className={`absolute inset-0 w-full h-10 px-3 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-200 hover:border-blue-500 transition-colors whitespace-nowrap overflow-hidden text-ellipsis ${mode === 'upload' ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            {isUploading ? 'Subiendo...' : 'Seleccionar / arrastrar imagen'}
          </button>
        </div>
      </div>

      <p className="text-[11px] leading-4 text-gray-500 dark:text-gray-300">Formatos: JPG, PNG, GIF, WEBP. Maximo: 5 MB.</p>

      {error && (
        <p className="mt-3 text-xs text-red-600 dark:text-red-300">{error}</p>
      )}

      {previewSrc && (
        <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-600 p-2 bg-gray-50 dark:bg-gray-700">
          <img
            src={previewSrc}
            alt="Vista previa de la imagen de la pregunta"
            className={`w-full ${previewMaxHeightClass} object-contain rounded`}
            onError={(e) => {
              e.target.style.display = 'none';
              setError('La imagen no ha podido cargarse. El archivo puede no existir en el servidor.');
            }}
          />
          <button
            type="button"
            disabled={disabled || isUploading}
            onClick={handleRemoveImage}
            className="mt-2 w-full py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
          >
            Quitar imagen
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
